<script lang="ts" setup>
import { LockOutlined, MailOutlined, UserOutlined } from '@ant-design/icons-vue'
import { toTypedSchema } from '@vee-validate/zod'
import { message } from 'ant-design-vue'
import { useField, useForm } from 'vee-validate'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { z } from 'zod'
import { parseInviteToken } from '@/lib/invites'
import { useAuthStore } from '@/stores/auth'

const auth = useAuthStore()
const router = useRouter()
const route = useRoute()
const authError = ref<string | null>(null)

const inviteToken = computed(() => (route.query.invite as string) || null)
const referralCode = computed(() => (route.query.ref as string) || null)
const inviteInfo = computed(() => {
  if (!inviteToken.value) return null
  return parseInviteToken(inviteToken.value)
})
const inviteHeader = computed(() => {
  if (!inviteInfo.value) return 'Create Account'
  return `Join ${inviteInfo.value.company_name}`
})

type RegisterForm = {
  name: string
  email: string
  password: string
  confirmPassword: string
}

const schema = z
  .object({
    name: z.string().nonempty('Name is required').min(2, 'Name must be at least 2 characters'),
    email: z.string().nonempty('Email is required').email('Please input a valid email!'),
    password: z
      .string()
      .nonempty('Password is required')
      .min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().nonempty('Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

const { handleSubmit, errors } = useForm<RegisterForm>({
  validationSchema: toTypedSchema(schema as any),
  initialValues: { name: '', email: '', password: '', confirmPassword: '' },
})

const { value: name } = useField<string>('name')
const { value: email } = useField<string>('email')
const { value: password } = useField<string>('password')
const { value: confirmPassword } = useField<string>('confirmPassword')

const loading = ref(false)

const onSubmit = handleSubmit(async (values) => {
  try {
    loading.value = true
    authError.value = null
    await auth.signUp(
      values.email,
      values.password,
      values.name,
      inviteToken.value,
      referralCode.value,
    )
    message.success('Account created! Check your email for the verification code.')
  } catch (err) {
    if (err instanceof Error && err.message) {
      authError.value = err.message
    } else {
      authError.value = 'Failed to create account.'
    }
  } finally {
    loading.value = false
  }
})

const disabled = computed(
  () => !name.value || !email.value || !password.value || !confirmPassword.value,
)
</script>
<template>
  <div class="register-container">
    <a-card class="register-form" :title="inviteHeader">
      <a-alert
        v-if="referralCode"
        type="success"
        show-icon
        style="margin-bottom: 16px"
      >
        <template #message>
          You were referred by <strong>{{ referralCode }}</strong>. Create your account to get started!
        </template>
      </a-alert>
      <a-alert
        v-if="inviteInfo"
        type="info"
        show-icon
        style="margin-bottom: 16px"
      >
        <template #message>
          You've been invited as <strong>{{ inviteInfo.role }}</strong>
          to join <strong>{{ inviteInfo.company_name }}</strong>.
        </template>
      </a-alert>
      <a-form
        name="normal_register"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
        @submit.prevent="onSubmit"
      >
        <a-form-item
          label="Name"
          :validate-status="errors.name ? 'error' : ''"
          :help="errors.name"
        >
          <a-input v-model:value="name" autocomplete="name">
            <template #prefix>
              <UserOutlined class="site-form-item-icon" />
            </template>
          </a-input>
        </a-form-item>

        <a-form-item
          label="Email"
          :validate-status="errors.email ? 'error' : ''"
          :help="errors.email"
        >
          <a-input v-model:value="email" autocomplete="email">
            <template #prefix>
              <MailOutlined class="site-form-item-icon" />
            </template>
          </a-input>
        </a-form-item>

        <a-form-item
          label="Password"
          :validate-status="errors.password ? 'error' : ''"
          :help="errors.password"
        >
          <a-input-password v-model:value="password" autocomplete="new-password">
            <template #prefix>
              <LockOutlined class="site-form-item-icon" />
            </template>
          </a-input-password>
        </a-form-item>

        <a-form-item
          label="Confirm"
          :validate-status="errors.confirmPassword ? 'error' : ''"
          :help="errors.confirmPassword"
        >
          <a-input-password v-model:value="confirmPassword" autocomplete="new-password">
            <template #prefix>
              <LockOutlined class="site-form-item-icon" />
            </template>
          </a-input-password>
        </a-form-item>

        <a-form-item v-if="authError" :wrapper-col="{ span: 24 }">
          <a-alert :message="authError" type="error" show-icon />
        </a-form-item>

        <a-form-item :wrapper-col="{ span: 24 }">
          <a-button
            :disabled="disabled"
            :loading="loading"
            type="primary"
            html-type="submit"
            class="register-form-button"
            block
          >
            Register
          </a-button>
        </a-form-item>

        <a-form-item :wrapper-col="{ span: 24 }">
          <div style="text-align: center">
            Already have an account?
            <router-link to="/login">Log in</router-link>
          </div>
        </a-form-item>
      </a-form>
    </a-card>
  </div>
</template>
<style scoped>
.register-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 85vh;
}

.register-form {
  max-width: 400px;
}
</style>
